/**
 * Pure, side-effect-free spectator reducer. Maps StreamDO wire frames onto a
 * small set of callbacks the Phaser/DOM glue implements. All Phaser, fetch and
 * WebSocket concerns live in `bootWatchingMode`; this file is unit-tested in
 * isolation (`test/watchController.test.ts`).
 *
 * Frame shapes (see `packages/game/src/worker/streamDO.ts`):
 *   { kind: 'offline' }
 *   { kind: 'catchup', gameId, turnId, keyframe, game, tableKey, rules,
 *     events: [{ seq, client_ts, server_ts, event }] }
 *   { kind: 'live', event: { seq, client_ts, server_ts, event } }
 *   { kind: 'boundary', gameId, turnId, keyframe, game, tableKey, rules }
 */

export interface WatchCallbacks {
  /** No producer connected — show the waiting/offline screen. */
  onOffline(): void;
  /** First keyframe (initial catchup or first boundary): boot the scene. */
  onBoot(ctx: { game: string; tableKey: string; rules: unknown; keyframe: unknown }): void;
  /** Apply one engine input event to the live engine. */
  onApply(event: { type: string; [k: string]: unknown }): void;
  /** Re-hydrate the engine to a keyframe (reconnect catchup OR new-turn boundary). */
  onReset(keyframe: unknown): void;
}

interface EventEnvelope {
  seq: number;
  client_ts: number;
  server_ts: number;
  event: { type: string; [k: string]: unknown };
}

interface CatchupFrame {
  kind: 'catchup';
  gameId: string;
  turnId: number;
  keyframe: unknown;
  game: string;
  tableKey: string;
  rules: unknown;
  events: EventEnvelope[];
}

interface BoundaryFrame {
  kind: 'boundary';
  gameId: string;
  turnId: number;
  keyframe: unknown;
  game: string;
  tableKey: string;
  rules: unknown;
}

interface LiveFrame {
  kind: 'live';
  event: EventEnvelope;
}

type Frame =
  | { kind: 'offline' }
  | CatchupFrame
  | BoundaryFrame
  | LiveFrame
  | { kind: string; [k: string]: unknown };

export interface WatchController {
  handleFrame(frame: Frame): void;
}

export function createWatchController(cbs: WatchCallbacks): WatchController {
  // Have we booted a scene yet? The first keyframe (catchup or boundary)
  // boots; subsequent keyframes re-hydrate the existing engine.
  let booted = false;
  // Highest event seq we've applied within the CURRENT turn. Reset to 0 on a
  // boundary (felt clears, the producer's seq counter restarts at 1). Used to
  // dedup overlapping catchup/live seqs on reconnect.
  let lastApplied = 0;

  const applyEvents = (events: EventEnvelope[]): void => {
    for (const e of events) cbs.onApply(e.event);
    lastApplied = events.length;
  };

  return {
    handleFrame(frame: Frame): void {
      switch (frame.kind) {
        case 'offline':
          cbs.onOffline();
          return;

        case 'catchup': {
          const f = frame as CatchupFrame;
          if (!booted) {
            cbs.onBoot({ game: f.game, tableKey: f.tableKey, rules: f.rules, keyframe: f.keyframe });
            booted = true;
            applyEvents(f.events);
          } else {
            // Reconnect: re-hydrate to the keyframe, then re-apply the buffer.
            cbs.onReset(f.keyframe);
            applyEvents(f.events);
          }
          return;
        }

        case 'boundary': {
          const f = frame as BoundaryFrame;
          if (!booted) {
            cbs.onBoot({ game: f.game, tableKey: f.tableKey, rules: f.rules, keyframe: f.keyframe });
            booted = true;
          } else {
            cbs.onReset(f.keyframe);
          }
          lastApplied = 0;
          return;
        }

        case 'live': {
          const f = frame as LiveFrame;
          // A `live` before any keyframe would desync the engine — ignore it.
          // The DO's invariant guarantees a keyframe precedes live, but be
          // defensive against reorderings on a flaky socket.
          if (!booted) return;
          if (f.event.seq <= lastApplied) return; // dedup
          cbs.onApply(f.event.event);
          lastApplied = f.event.seq;
          return;
        }

        default:
          // 'hello', 'ack', 'resync' and other producer-side frames never
          // reach a watcher; ignore anything unrecognized.
          return;
      }
    },
  };
}
